import { REPORT_PERIODS } from './steps/ReportStepEditor.js';
import { Fragment } from 'react';
import type { BuilderStep } from '../../lib/types.js';
import { OPS } from './conditionFields.js';
import { Pill } from './controls/OutputValue.js';
import { type PickerVariable } from './definitionEdit.js';
import { parseTemplate } from './tokenText.js';
import { useBotStepOptions, useReportStepOptions } from './steps/stepOptions.js';

const Verb = ({ children }: { children: React.ReactNode }) => (
  <span className="w-16 flex-none text-[0.68rem] font-semibold uppercase tracking-wider text-ink-subtle">
    {children}
  </span>
);

/** Friendly rendering of a stored path, matched against picker vars when possible. */
const pathLabel = (path: string, vars: PickerVariable[]): string =>
  vars.find((v) => v.path === path)?.label ?? path;

/**
 * A stored template as it reads: the same chips the editor shows, so the
 * collapsed row never puts {{payload.unitId}} in front of anyone. Unresolvable
 * references stay visible — struck through — rather than reading as plain text.
 */
const Templated = ({ text, vars }: { text: string; vars: PickerVariable[] }) => (
  <>
    {parseTemplate(text, vars).map((seg, i) =>
      seg.kind === 'text' ? (
        <span key={i}>{seg.text}</span>
      ) : (
        <span
          key={i}
          className={`mx-0.5 inline-flex items-center rounded border px-1 align-baseline text-xs font-medium ${
            seg.known
              ? 'border-info-border bg-info-soft text-info'
              : 'border-danger-border bg-danger-soft text-danger line-through'
          }`}
        >
          {seg.label}
        </span>
      ),
    )}
  </>
);

/** A report step names its report, from the same cached list its editor uses. */
const ReportSentence = ({ step }: { step: Extract<BuilderStep, { type: 'report' }> }) => {
  const { reportOptions } = useReportStepOptions();
  const who = step.recipients.length;
  // Values from the event read as the field they come from, not the raw template.
  const narrowed = [step.customer, step.region, step.units]
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v) => (v.includes('{{') ? 'the event’s value' : v));
  return (
    <>
      <Verb>Report</Verb>
      {step.templateId ? (
        <>
          Run{' '}
          <Pill>{reportOptions.find((r) => r.value === step.templateId)?.label ?? 'a report'}</Pill>{' '}
          for{' '}
          {narrowed.length > 0 && (
            <>
              <Pill>{narrowed.join(', ')}</Pill>,{' '}
            </>
          )}
          <Pill>
            {REPORT_PERIODS.find((p) => p.value === step.period)?.label ??
              (step.period || 'a period')}
          </Pill>{' '}
          <span className="text-xs text-ink-subtle">
            ·{' '}
            {step.sendLink
              ? `sends the link to ${who === 1 ? 'one person' : `${String(who)} people`}`
              : 'no link sent'}
          </span>
        </>
      ) : (
        <span className="text-ink-subtle">Pick a report and who gets it…</span>
      )}
    </>
  );
};

/** A bot step names its bot and group, from the same cached lists its editor uses. */
const BotSentence = ({
  step,
  vars,
}: {
  step: Extract<BuilderStep, { type: 'bot' }>;
  vars: PickerVariable[];
}) => {
  const { botOptions, groupOptions } = useBotStepOptions();
  const bot = botOptions.find((b) => b.value === step.botId)?.label;
  const r = step.recipients;
  // Group options read "Mechanics · 4 people"; the sentence wants the name.
  const groupName = (id: string) =>
    groupOptions.find((g) => g.value === id)?.label.split(' · ')[0] ?? 'a group';
  const fieldName = (path: string) => vars.find((v) => v.path === path)?.label ?? 'the event';
  const who =
    r.kind === 'group'
      ? `everyone in ${groupName(r.groupId)}`
      : r.field
        ? `the person named in ${fieldName(r.field)}`
        : '';
  return (
    <>
      <Verb>Bot</Verb>
      {step.botId && who ? (
        <>
          <Pill>{bot ?? 'A bot'}</Pill> {step.ask ? 'asks' : 'tells'} <Pill>{who}</Pill>
          {step.ask && ' to decide'}
        </>
      ) : (
        <span className="text-ink-subtle">Choose a bot and who it tells…</span>
      )}
    </>
  );
};

export const Sentence = ({
  step,
  vars,
  destinations = [],
}: {
  step: BuilderStep;
  vars: PickerVariable[];
  /** The picker's own options — so the sentence can NAME where a message
   *  goes instead of only counting ("to 5 destinations", none named). */
  destinations?: { value: string; label: string }[];
}) => {
  const destinationsForStep =
    step.type === 'notify'
      ? [
          ...step.channelIds,
          ...(step.recipients ?? []).map((recipient) => `${recipient.kind}:${recipient.id}`),
        ]
      : [];
  switch (step.type) {
    case 'filter':
      return (
        <>
          <Verb>Filter</Verb> Continue only if{' '}
          {step.conditions.map((c, i) => (
            <Fragment key={i}>
              {i > 0 && (
                <span className="text-ink-muted">{step.match === 'all' ? ' and ' : ' or '}</span>
              )}
              <Pill>{pathLabel(c.path, vars) || 'a field'}</Pill>{' '}
              <Pill>{OPS.find((o) => o.value === c.op)?.label}</Pill>{' '}
              {c.op !== 'exists' && (
                <Pill>{String(c.value ?? '').trim() ? String(c.value) : 'a value'}</Pill>
              )}
            </Fragment>
          ))}
        </>
      );
    case 'notify':
      return (
        <>
          <Verb>Message</Verb>
          {step.title ? (
            <>
              Send “
              <Templated text={step.title} vars={vars} />”
              {destinationsForStep.length > 0 && (
                <span className="text-xs text-ink-subtle">
                  {' '}
                  · to{' '}
                  {(() => {
                    const names = destinationsForStep
                      .map((id) => destinations.find((d) => d.value === id)?.label)
                      .filter((n): n is string => Boolean(n));
                    const rest = destinationsForStep.length - Math.min(names.length, 2);
                    if (names.length === 0)
                      return `${String(destinationsForStep.length)} ${destinationsForStep.length === 1 ? 'destination' : 'destinations'}`;
                    return `${names.slice(0, 2).join(', ')}${rest > 0 ? ` and ${String(rest)} more` : ''}`;
                  })()}
                </span>
              )}
            </>
          ) : (
            <span className="text-ink-subtle">Write the message and choose who gets it…</span>
          )}
        </>
      );
    case 'agent': {
      // A label, not the document: the first line of the goal, trimmed. The
      // full task (and its report-back points) lives in the editor. Agent
      // tasks are verbatim — never templated — so no chip rendering here.
      const firstLine = step.task.split('\n')[0]?.trim() ?? '';
      const excerpt =
        firstLine.length > 140
          ? `${firstLine.slice(0, firstLine.lastIndexOf(' ', 140))}…`
          : firstLine;
      const points = step.reportOn?.filter((p) => p.trim()).length ?? 0;
      return (
        <>
          <Verb>AI</Verb>
          {excerpt ? (
            <>
              <span className="italic">“{excerpt}”</span>{' '}
              <span className="text-xs text-ink-subtle">
                · investigates, then answers
                {points > 0 ? ` · ${String(points)}-point answer checklist` : ''}
              </span>
            </>
          ) : (
            'Have an AI agent investigate'
          )}
        </>
      );
    }
    case 'approval': {
      const who =
        step.approvers.length === 0
          ? null
          : step.approvers.length === 1
            ? 'one person'
            : `${String(step.approvers.length)} people`;
      return (
        <>
          <Verb>Approve</Verb>
          {who && step.title ? (
            <>
              Ask <Pill>{who}</Pill> “{step.title}”{' '}
              <span className="text-xs text-ink-subtle">· waits for an answer</span>
            </>
          ) : (
            <span className="text-ink-subtle">Choose who to ask and what to ask them…</span>
          )}
        </>
      );
    }
    case 'report':
      return <ReportSentence step={step} />;
    case 'bot':
      return <BotSentence step={step} vars={vars} />;
    case 'branch':
      return (
        <>
          <Verb>Split</Verb>{' '}
          <span className="text-xs text-ink-muted">
            {step.cases.map((c) => c.label).join(' / ')} / Otherwise · first match wins
          </span>
        </>
      );
    // A definition saved under an OLDER step vocabulary must still read as a
    // row (same guard as summarize's clause) — a blank bar looks broken.
    default:
      return (
        <>
          <Verb>Step</Verb>
          <span className="text-ink-subtle">A step this version no longer supports</span>
        </>
      );
  }
};
